import React, { useState, useEffect } from 'react';
import { logsService } from '../../services/logsService';
import '../../styles/LogsManager.css';

export default function LogsManager() {
  const [logs, setLogs] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [filters, setFilters] = useState({
    season: '',
    userEmail: '',
    actionType: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [seasonsResult, typesResult, logsResult] = await Promise.all([
        logsService.getSeasons(),
        logsService.getActionTypes(),
        logsService.fetchLogs({ limit: 500 })
      ]);

      if (seasonsResult.data) setSeasons(seasonsResult.data);
      if (typesResult.data) setActionTypes(typesResult.data);
      if (logsResult.data) setLogs(logsResult.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load logs' });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = async () => {
    setLoading(true);
    try {
      const result = await logsService.fetchLogs(filters);
      if (result.data) {
        setLogs(result.data);
        setMessage({ type: 'success', text: `Found ${result.data.length} logs` });
      } else if (result.error) {
        setMessage({ type: 'error', text: 'Failed to fetch logs' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred while fetching logs' });
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setFilters({
      season: '',
      userEmail: '',
      actionType: '',
      startDate: '',
      endDate: ''
    });
    loadInitialData();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDetails = (details) => {
    if (typeof details === 'string') {
      try {
        return JSON.stringify(JSON.parse(details), null, 2);
      } catch {
        return details;
      }
    }
    return JSON.stringify(details, null, 2);
  };

  return (
    <div className="logs-manager">
      <div className="logs-section">
        <h3>Logs & Audit Trail</h3>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="filters-section">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Season</label>
              <select
                name="season"
                value={filters.season}
                onChange={handleFilterChange}
              >
                <option value="">All Seasons</option>
                {seasons.map(season => (
                  <option key={season} value={season}>
                    {season}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>User Email</label>
              <input
                type="text"
                name="userEmail"
                value={filters.userEmail}
                onChange={handleFilterChange}
                placeholder="Search by email..."
              />
            </div>

            <div className="filter-group">
              <label>Action Type</label>
              <select
                name="actionType"
                value={filters.actionType}
                onChange={handleFilterChange}
              >
                <option value="">All Actions</option>
                {actionTypes.map(type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="datetime-local"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group">
              <label>End Date</label>
              <input
                type="datetime-local"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </div>
          </div>

          <div className="filter-buttons">
            <button
              className="btn-primary"
              onClick={applyFilters}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Apply Filters'}
            </button>
            <button
              className="btn-secondary"
              onClick={resetFilters}
              disabled={loading}
            >
              Reset
            </button>
          </div>
        </div>

        <div className="logs-table-container">
          {loading ? (
            <div className="loading">Loading logs...</div>
          ) : logs.length === 0 ? (
            <div className="no-logs">No logs found</div>
          ) : (
            <table className="logs-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Season</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, index) => (
                  <tr key={index}>
                    <td>{formatDate(log.created_at)}</td>
                    <td>{log.user_email}</td>
                    <td>
                      <span className={`action-badge action-${log.action_type.toLowerCase().replace(/\s+/g, '-')}`}>
                        {log.action_type}
                      </span>
                    </td>
                    <td>{log.season || '-'}</td>
                    <td className="details-cell">
                      <pre className="details-pre">{formatDetails(log.details)}</pre>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="logs-footer">
          <p>Showing {logs.length} log(s)</p>
        </div>
      </div>
    </div>
  );
}
